import { useMemo } from 'react';
// eslint-disable-next-line no-unused-vars
import { motion } from 'framer-motion';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

export function ComplianceSummaryCard({
  gapAnalysis = [],
  gapAnalysisCounts = {},
  onViewDetails,
}) {
  const { shortageCount = 0, metCount = 0, shortageItems = [] } = gapAnalysisCounts;

  const percentage = useMemo(() => {
    if (gapAnalysis.length === 0) return 100;
    return Math.round((metCount / gapAnalysis.length) * 100);
  }, [gapAnalysis, metCount]);

  const ringColor =
    percentage >= 80
      ? 'text-emerald-500'
      : percentage >= 50
        ? 'text-amber-500'
        : 'text-red-500';

  const ringStrokeColor =
    percentage >= 80
      ? 'stroke-emerald-500'
      : percentage >= 50
        ? 'stroke-amber-500'
        : 'stroke-red-500';

  // SVG ring geometry
  const radius = 28;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;

  const displayShortages = shortageItems?.slice(0, 2) || [];

  return (
    <Card className="py-0">
      <CardContent className="p-4">
        <div className="flex flex-col items-center gap-3">
          {/* SVG Circular Progress Ring */}
          <div className="relative">
            <svg
              viewBox="0 0 64 64"
              className="h-20 w-20 -rotate-90"
            >
              {/* Background circle */}
              <circle
                cx="32"
                cy="32"
                r={radius}
                fill="none"
                stroke="currentColor"
                strokeWidth="4"
                className="text-muted/30"
              />
              {/* Progress circle */}
              <motion.circle
                cx="32"
                cy="32"
                r={radius}
                fill="none"
                strokeWidth="4"
                strokeLinecap="round"
                className={ringStrokeColor}
                strokeDasharray={circumference}
                initial={{ strokeDashoffset: circumference }}
                animate={{ strokeDashoffset }}
                transition={{ duration: 1, ease: 'easeOut' }}
              />
            </svg>
            {/* Center percentage text */}
            <div className="absolute inset-0 flex items-center justify-center">
              <span className={cn('text-lg font-bold', ringColor)}>
                {percentage}%
              </span>
            </div>
          </div>

          {/* Summary text */}
          <p className="text-xs text-muted-foreground text-center">
            {metCount} met, {shortageCount} gaps
          </p>

          {/* Shortage badges */}
          {displayShortages.length > 0 && (
            <div className="flex flex-wrap justify-center gap-1">
              {displayShortages.map((item, index) => (
                <Badge
                  key={index}
                  variant="destructive"
                  className="text-[10px] px-1.5 py-0"
                >
                  {item.name || 'Unknown'}
                </Badge>
              ))}
            </div>
          )}

          {/* View details link */}
          <Button
            variant="ghost"
            size="sm"
            onClick={onViewDetails}
            className="text-xs h-7 px-2 text-muted-foreground hover:text-foreground"
          >
            View Live Compliance &rarr;
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
